import api from '../../../lib/api'
import type { BillingService, Subscription, ActiveFeatures, PricingCatalog, ServicePricing } from '../../../types'

export const billingService = {
  // ── Service catalog ────────────────────────────────────────────────────────

  async listServices(): Promise<BillingService[]> {
    const { data } = await api.get('/subscriptions/services')
    return data
  },

  async createService(payload: { name: string; slug: string; service_type: string; description?: string }): Promise<BillingService> {
    const { data } = await api.post('/subscriptions/services', payload)
    return data
  },

  async updateService(id: number, payload: Partial<{ name: string; description: string; is_active: boolean }>): Promise<BillingService> {
    const { data } = await api.patch(`/subscriptions/services/${id}`, payload)
    return data
  },

  // ── Pricing catalog ────────────────────────────────────────────────────────

  async getPricing(): Promise<PricingCatalog> {
    const { data } = await api.get('/subscriptions/pricing')
    return data
  },

  async getPricingBySlug(slug: string): Promise<ServicePricing> {
    const { data } = await api.get(`/subscriptions/pricing/${slug}`)
    return data
  },

  // ── Subscription lifecycle (BACKEND_ADMIN) ─────────────────────────────────

  async subscribe(payload: { tenant_id: number; service_id: number; billing_cycle?: string; pricing_tier?: string }): Promise<Subscription> {
    const { data } = await api.post('/subscriptions', payload)
    return data
  },

  async listSubscriptions(): Promise<Subscription[]> {
    const { data } = await api.get('/subscriptions/all')
    return data
  },

  async getTenantSubscriptions(tenantId: number): Promise<Subscription[]> {
    const { data } = await api.get(`/subscriptions/tenant/${tenantId}`)
    return data
  },

  async suspendSubscription(id: number): Promise<Subscription> {
    const { data } = await api.patch(`/subscriptions/${id}/suspend`)
    return data
  },

  async reactivateSubscription(id: number): Promise<Subscription> {
    const { data } = await api.patch(`/subscriptions/${id}/reactivate`)
    return data
  },

  async cancelSubscription(id: number): Promise<Subscription> {
    const { data } = await api.patch(`/subscriptions/${id}/cancel`)
    return data
  },

  async assignTier(id: number, payload: { pricing_tier: string; billing_cycle: string }): Promise<Subscription> {
    // Backend expects 'tier' (not 'pricing_tier') and billing_cycle in UPPERCASE
    const { data } = await api.post(`/subscriptions/${id}/assign-tier`, {
      tier: payload.pricing_tier,
      billing_cycle: payload.billing_cycle.toUpperCase(),
    })
    return data
  },

  async runBilling(): Promise<{ processed: number }> {
    const { data } = await api.post('/subscriptions/billing-run')
    return data
  },

  // ── Dealer Principal self-service ──────────────────────────────────────────

  async getMySubscriptions(): Promise<Subscription[]> {
    const { data } = await api.get('/subscriptions/mine')
    return data
  },

  async getMyFeatures(): Promise<ActiveFeatures> {
    const { data } = await api.get('/subscriptions/my-features')
    return data
  },
}
